// PageHeader and Breadcrumb, adapted from design/isomorphic/src/core/page-header.tsx (from
// Isomorphic's shared/page-header.tsx and its circle-separator breadcrumb). The title is the
// page's h1.
import { Fragment, type ReactNode } from "react";
import { Link } from "react-router";

import { useT } from "../i18n";
import { cn } from "./cn";

export interface Crumb {
  name: string;
  href?: string;
}

export function Breadcrumb({ items }: { items: Crumb[] }) {
  const t = useT();
  return (
    <nav className="flex flex-wrap items-center gap-2 text-[13px]" aria-label={t.common.breadcrumb}>
      {items.map((item, i) => (
        <Fragment key={item.name}>
          {i > 0 ? <span className="h-1 w-1 rounded-full bg-gray-300" aria-hidden /> : null}
          {item.href ? (
            <Link to={item.href} className="text-gray-700 hover:text-primary">
              {item.name}
            </Link>
          ) : (
            <span className="text-gray-500">{item.name}</span>
          )}
        </Fragment>
      ))}
    </nav>
  );
}

export function PageHeader({
  title,
  breadcrumb,
  description,
  children,
  className,
}: {
  title: ReactNode;
  breadcrumb?: Crumb[];
  description?: ReactNode;
  children?: ReactNode;
  className?: string;
}) {
  return (
    <header className={cn("mb-6 @container lg:mb-7", className)}>
      <div className="flex flex-col gap-4 @2xl:flex-row @2xl:items-end @2xl:justify-between">
        <div className="min-w-0">
          {breadcrumb ? <Breadcrumb items={breadcrumb} /> : null}
          <h1 className={cn("text-[22px] tracking-tight lg:text-2xl", breadcrumb && "mt-1.5")}>{title}</h1>
          {description ? <p className="mb-0 mt-1 max-w-2xl break-words text-[13.5px] text-gray-500">{description}</p> : null}
        </div>
        {children ? <div className="flex shrink-0 flex-wrap items-center gap-2.5">{children}</div> : null}
      </div>
    </header>
  );
}
